function stripTrailingSlash(s: string): string {
  return s.replace(/\/+$/, '');
}

/**
 * База API без завершающего слэша.
 * Если задан NEXT_PUBLIC_API_URL — используется везде (браузер и SSR), иначе в браузере same-origin,
 * на сервере — localhost:8000.
 *
 * Нужно явно задавать URL, когда на том же домене /api всё ещё отдаёт старый Django, а новый API
 * на другом origin (прокси на 8001, отдельный хост и т.д.).
 */
function getApiBase(): string {
  const fromEnv =
    typeof process !== 'undefined' && process.env.NEXT_PUBLIC_API_URL
      ? stripTrailingSlash(process.env.NEXT_PUBLIC_API_URL)
      : '';
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined') {
    return '';
  }
  return 'http://localhost:8000';
}

export interface ApiCategory {
  id: number;
  slug: string;
  name: string;
  productCount?: number;
  coverImage?: string;
}

export interface PurchaseOption {
  type: 'pack' | 'box';
  label: string;
  unitsPerOption: number;
  /** Розничная цена за единицу опции (упаковка или коробка целиком). */
  basePrice: number;
  /** Оптовая цена за ту же единицу. */
  wholesalePrice?: number;
}

export type PriceMode = 'retail' | 'wholesale';

export function unitPriceForOption(opt: PurchaseOption | undefined, mode: PriceMode): number {
  if (!opt) return 0;
  if (mode === 'wholesale' && opt.wholesalePrice != null) {
    return opt.wholesalePrice;
  }
  return opt.basePrice;
}

export interface ApiProduct {
  id: string;
  name: string;
  category: string;
  categoryName: string;
  image: string;
  alt: string;
  purchaseOptions: PurchaseOption[];
  minPackQuantity: number;
  minBoxQuantity: number;
  inStock: boolean;
  popularity: number;
}

export interface ApiOrderCreatePayload {
  contactPerson: string;
  phone: string;
  email: string;
  deliveryAddress: string;
  city: string;
  preferredDeliveryDate: string | null;
  deliveryTimeframe: string;
  cartItems: Array<{
    productId: string;
    quantity: number;
    optionType: 'pack' | 'box';
  }>;
  promoCode?: string;
}

export interface ApiOrderCreateResponse {
  orderId: number;
  orderNumber: string;
}

export interface ApiOrderItem {
  id: number;
  name: string;
  quantity: number;
  unit: string;
  pricePerUnit: string;
  totalPrice: string;
}

export interface ApiOrderDetail {
  id: number;
  orderNumber: string;
  status: string;
  contactPerson: string;
  phone: string;
  email: string;
  deliveryAddress: string;
  city: string;
  preferredDeliveryDate: string | null;
  deliveryTimeframe: string;
  items: ApiOrderItem[];
  totalAmount: string | number;
  createdAt: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const base = getApiBase();
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  const text = await res.text();
  let data: T | undefined;
  try {
    data = text ? (JSON.parse(text) as T) : undefined;
  } catch {
    // ignore
  }
  if (!res.ok) {
    throw new ApiError(
      (data && typeof data === 'object' && 'detail' in data
        ? String((data as { detail?: string }).detail)
        : res.statusText) || `HTTP ${res.status}`,
      res.status,
      data
    );
  }
  return data as T;
}

export interface PaginatedProductsResponse {
  count: number;
  page: number;
  page_size: number;
  has_more: boolean;
  results: ApiProduct[];
}

export interface GetProductsPageParams {
  page?: number;
  page_size?: number;
  /** Slug категорий; на бэкенд уходит как несколько `category` */
  categories?: string[];
  search?: string;
  in_stock?: boolean;
  sort_by?: string;
  price_min?: number;
  price_max?: number;
  signal?: AbortSignal;
}

function appendCategoryParams(search: URLSearchParams, categories?: string[]) {
  if (!categories?.length) return;
  for (const slug of categories) {
    if (slug) search.append('category', slug);
  }
}

export function getCategories(): Promise<ApiCategory[]> {
  return request<ApiCategory[]>('/api/categories/');
}

export function getProductsPage(
  params?: GetProductsPageParams
): Promise<PaginatedProductsResponse> {
  const search = new URLSearchParams();
  const page = params?.page ?? 1;
  search.set('page', String(page));
  search.set('page_size', String(params?.page_size ?? 24));
  appendCategoryParams(search, params?.categories);
  if (params?.search) search.set('search', params.search);
  if (params?.in_stock !== undefined) search.set('in_stock', String(params.in_stock));
  if (params?.sort_by) search.set('sort_by', params.sort_by);
  if (params?.price_min !== undefined) search.set('price_min', String(params.price_min));
  if (params?.price_max !== undefined) search.set('price_max', String(params.price_max));
  const qs = search.toString();
  return request<PaginatedProductsResponse>(`/api/products/?${qs}`, {
    signal: params?.signal,
  });
}

export function getProductsByIds(ids: string[], signal?: AbortSignal): Promise<ApiProduct[]> {
  const clean = [...new Set(ids.map((x) => x.trim()).filter(Boolean))];
  if (clean.length === 0) return Promise.resolve([]);
  const q = new URLSearchParams({ ids: clean.join(',') });
  return request<PaginatedProductsResponse>(`/api/products/?${q}`, { signal }).then((r) => r.results);
}

export function getFeaturedProducts(signal?: AbortSignal): Promise<ApiProduct[]> {
  return getProductsPage({
    page: 1,
    page_size: 4,
    in_stock: true,
    sort_by: 'name-asc',
    signal,
  }).then((r) => r.results);
}

export function getProduct(id: string): Promise<ApiProduct> {
  return request<ApiProduct>(`/api/products/${id}/`);
}

export function createOrder(payload: ApiOrderCreatePayload): Promise<ApiOrderCreateResponse> {
  return request<ApiOrderCreateResponse>('/api/orders/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getOrder(id: string | number): Promise<ApiOrderDetail> {
  return request<ApiOrderDetail>(`/api/orders/${id}/`);
}

export function checkWholesalePromoCode(code: string): Promise<{ valid: boolean }> {
  const q = new URLSearchParams({ code: code.trim() });
  return request<{ valid: boolean }>(`/api/wholesale-promo/check/?${q}`);
}

const WHOLESALE_PROMO_STORAGE_KEY = 'wholesalePromoCode';

export function getStoredWholesalePromoCode(): string {
  if (typeof window === 'undefined') return '';
  try {
    return localStorage.getItem(WHOLESALE_PROMO_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function setStoredWholesalePromoCode(code: string): void {
  if (typeof window === 'undefined') return;
  const t = code.trim();
  if (!t) {
    localStorage.removeItem(WHOLESALE_PROMO_STORAGE_KEY);
    return;
  }
  localStorage.setItem(WHOLESALE_PROMO_STORAGE_KEY, t);
}

export function clearStoredWholesalePromoCode(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(WHOLESALE_PROMO_STORAGE_KEY);
}
